import type { LearningExercise } from "../types";

// Arbeitnehmererfindungsrecht — ArbnErfG
// Abdeckung: § 2 (Erfindungsbegriff), § 4 (Diensterfindung vs. freie Erfindung),
// § 5 (Meldepflicht), § 6 (fiktive Inanspruchnahme, 4-Monats-Frist — KERNTHEMA),
// § 7 (Wirkung der Inanspruchnahme), §§ 9-12 (Vergütung, angemessene Vergütung,
// Lizenzanalogie), §§ 13-14 (Schutzrechtsanmeldung), § 16 (Aufgabe, Anbietungspflicht),
// §§ 18-19 (freie Erfindungen: Mitteilungs- und Anbietungspflicht),
// §§ 28-30 (Schiedsstelle beim DPMA), § 42 (Hochschulerfindungen).
// Quellen inline (sources-Array), pathId "deutsche-ausbildung", mode "ausbildung".
// IDs: da-arbnerf-001 … da-arbnerf-038.
// Fallkonstellationen: da-arbnerf-010, -011, -014, -017, -019, -021, -023, -025, -027,
//   -029, -032, -034, -036 (≥ 34 % — Pflicht ≥ 30 %)
// reviewStatus "draft" für ALLE Items — Freigabe ausschließlich durch question-reviewer.

export const ausbildungArbnErf: LearningExercise[] = [

  // ─── FLASHCARDS da-arbnerf-001 … da-arbnerf-008 ────────────────────────────

  {
    id: "da-arbnerf-001",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "ArbnErfG § 4 — Diensterfindung vs. freie Erfindung",
    title: "Abgrenzung Diensterfindung / freie Erfindung",
    prompt:
      "Welche zwei alternativen Tatbestandsvarianten begründen nach § 4 Abs. 2 ArbnErfG eine Diensterfindung, und was ist der Unterschied zur freien Erfindung?",
    options: [],
    answers: [],
    explanation:
      "§ 4 Abs. 2 ArbnErfG: Eine Erfindung ist Diensterfindung, wenn sie entweder (1) während der Dauer des Arbeitsverhältnisses aus der dem Arbeitnehmer obliegenden Tätigkeit entstanden ist (betriebliche Entstehung — 'Aufgabenerfindung'), oder (2) maßgeblich auf Erfahrungen oder Arbeiten des Betriebes beruht (betriebliche Ressourcen — 'Erfahrungserfindung'). Beide Varianten stehen alternativ nebeneinander; eine Erfindung ist also Diensterfindung, wenn auch nur eine der Varianten erfüllt ist. Ist keine der Varianten erfüllt, ist die Erfindung eine freie Erfindung (§ 4 Abs. 3 ArbnErfG), für die der Arbeitnehmer das volle Recht behält, aber ggf. Mitteilungs- und Anbietungspflichten nach §§ 18-19 ArbnErfG bestehen.",
    sourceIds: ["arbnerfg4"],
    sources: [
      {
        id: "arbnerfg4",
        label: "§ 4 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__4.html",
      },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["arbnerf", "diensterfindung", "freie-erfindung"],
    // REVIEW (question-reviewer): REVIEWED — § 4 Abs. 2 (Var. 1 Aufgabenerfindung,
    // Var. 2 Erfahrungserfindung; alternativ nebeneinander) und § 4 Abs. 3 (freie
    // Erfindung) fachlich korrekt; §§ 18-19-Hinweis zutreffend. Keine Normfehler,
    // Quelle vorhanden. Freigabe.
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-002",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "ArbnErfG § 6 — Fiktive Inanspruchnahme (Kernthema)",
    title: "4-Monats-Frist und fiktive Inanspruchnahme (§ 6 Abs. 2 ArbnErfG)",
    prompt:
      "Was versteht man unter der 'fiktiven Inanspruchnahme' nach § 6 Abs. 2 ArbnErfG, und welche Frist ist maßgeblich?",
    options: [],
    answers: [],
    explanation:
      "§ 6 Abs. 2 ArbnErfG (Fassung seit PatG-Reform 2009): Eine Diensterfindung gilt als in Anspruch genommen, wenn der Arbeitgeber sie nicht innerhalb von vier Monaten nach Eingang der ordnungsgemäßen Meldung (§ 5 ArbnErfG) durch Erklärung in Textform (§ 126b BGB) freigibt. Das bedeutet: Der Arbeitgeber muss nicht aktiv erklären, dass er die Erfindung in Anspruch nimmt — Schweigen führt automatisch zur Inanspruchnahme (Fiktionswirkung). Vor 2009 musste der Arbeitgeber die Erfindung aktiv und unbeschränkt in Anspruch nehmen; seit 2009 ist Schweigen = Inanspruchnahme. Die 4-Monats-Frist beginnt mit dem Eingang der ordnungsgemäßen Meldung beim Arbeitgeber.",
    // FIX (verifiziert): "schriftliche Erklärung" → "Erklärung in Textform (§ 126b BGB)"
    sourceIds: ["arbnerfg6", "bgb126b"],
    sources: [
      {
        id: "arbnerfg6",
        label: "§ 6 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__6.html",
      },
      {
        id: "bgb126b",
        label: "§ 126b BGB",
        url: "https://www.gesetze-im-internet.de/bgb/__126b.html",
      },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["arbnerf", "inanspruchnahme", "4-monats-frist"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-003",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "ArbnErfG § 5 — Meldepflicht",
    title: "Inhalt und Form der Meldung einer Diensterfindung (§ 5 ArbnErfG)",
    prompt:
      "Welche formalen und inhaltlichen Anforderungen stellt § 5 ArbnErfG an die Meldung einer Diensterfindung?",
    options: [],
    answers: [],
    explanation:
      "§ 5 Abs. 1 ArbnErfG: Der Arbeitnehmer ist verpflichtet, eine Diensterfindung unverzüglich dem Arbeitgeber gesondert in Textform (§ 126b BGB) zu melden. 'Gesondert' bedeutet: Die Meldung muss als solche erkennbar sein und darf nicht in allgemeinen Berichten oder Gesprächen untergehen. § 5 Abs. 2 ArbnErfG: In der Meldung hat der Arbeitnehmer die technische Aufgabe, Lösung und das Zustandekommen der Diensterfindung so zu beschreiben, dass der Arbeitgeber beurteilen kann, ob es sich um eine Diensterfindung handelt. § 5 Abs. 3 ArbnErfG: Der Arbeitgeber hat den Zeitpunkt des Eingangs der Meldung unverzüglich in Textform zu bestätigen. Hat die Meldung die nach Absatz 2 erforderlichen Angaben nicht, so hat der Arbeitgeber dies dem Arbeitnehmer unverzüglich in Textform mitzuteilen. Wichtig: Erklärt der Arbeitgeber nicht binnen zwei Monaten in Textform, dass und in welcher Hinsicht die Meldung ergänzungsbedürftig ist, gilt die Meldung als ordnungsgemäß (§ 5 Abs. 3 Satz 2 — Heilungsfiktion).",
    sourceIds: ["arbnerfg5", "bgb126b"],
    sources: [
      {
        id: "arbnerfg5",
        label: "§ 5 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__5.html",
      },
      {
        id: "bgb126b",
        label: "§ 126b BGB",
        url: "https://www.gesetze-im-internet.de/bgb/__126b.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["arbnerf", "meldepflicht"],
    // FIX (verifiziert): "schriftlich" → "in Textform (§ 126b BGB)"; § 5 Abs. 3 präzisiert
    // (Textform-Bestätigung + 2-Monats-Heilungsfiktion § 5 Abs. 3 S. 2 ergänzt).
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-004",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "ArbnErfG §§ 9-12 — Vergütung",
    title: "Angemessene Vergütung nach § 9 ArbnErfG — Berechnungsfaktoren",
    prompt:
      "Welche Faktoren bestimmen nach § 9 Abs. 2 ArbnErfG die Höhe der angemessenen Vergütung des Arbeitnehmers für eine in Anspruch genommene Diensterfindung?",
    options: [],
    answers: [],
    explanation:
      "§ 9 Abs. 2 ArbnErfG nennt drei Hauptfaktoren für die angemessene Vergütung: (1) die wirtschaftliche Verwertbarkeit der Diensterfindung — insbesondere der Erfindungswert (Marktwert, Lizenzumsatz, Einsparungen); (2) die Aufgaben und die Stellung des Arbeitnehmers im Betrieb — Arbeitnehmer in Forschungs-/Entwicklungsabteilungen haben eine geringere 'Anteilsquote' als solche, von denen Erfinden nicht erwartet wird; (3) der Anteil des Betriebs an dem Zustandekommen der Diensterfindung — Betriebsmittel, Know-how, Vorarbeiten. Die Vergütungsrichtlinien (§ 11 ArbnErfG) konkretisieren diese Faktoren. Klassische Berechnungsmethoden: Lizenzanalogie (häufigste Methode), erfassbarer betrieblicher Nutzen, Schätzung.",
    sourceIds: ["arbnerfg9"],
    sources: [
      {
        id: "arbnerfg9",
        label: "§ 9 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__9.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["arbnerf", "verguetung", "lizenzanalogie"],
    // REVIEW (question-reviewer): REVIEWED — die drei Faktoren des § 9 Abs. 2 ArbnErfG
    // (wirtschaftliche Verwertbarkeit/Erfindungswert, Aufgaben und Stellung des AN,
    // Anteil des Betriebs am Zustandekommen) korrekt wiedergegeben; Verweis auf § 11
    // (Vergütungsrichtlinien) und Lizenzanalogie als häufigste Methode zutreffend.
    // Keine Normfehler. Freigabe.
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-005",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "ArbnErfG §§ 18-19 — Freie Erfindungen",
    title: "Mitteilungs- und Anbietungspflicht bei freien Erfindungen (§§ 18-19 ArbnErfG)",
    prompt:
      "Welche Pflichten hat ein Arbeitnehmer bezüglich seiner freien Erfindungen nach §§ 18, 19 ArbnErfG, und warum bestehen diese trotz der Einordnung als 'frei'?",
    options: [],
    answers: [],
    explanation:
      "§ 18 ArbnErfG (Mitteilungspflicht): Der Arbeitnehmer muss jede freie Erfindung dem Arbeitgeber vor ihrer Verwertung in Textform (§ 126b BGB) mitteilen. Die Mitteilungspflicht gilt für alle während des Arbeitsverhältnisses gemachten freien Erfindungen; eine Ausnahme besteht nur dann, wenn die Erfindung offensichtlich im Arbeitsbereich des Betriebes nicht verwendbar ist (§ 18 Abs. 3 ArbnErfG). Entscheidend: Der 'sachliche Betriebsbezug' ist NICHT Voraussetzung der Mitteilungspflicht nach § 18, sondern Voraussetzung der ANBIETUNGSpflicht nach § 19. § 19 ArbnErfG (Anbietungspflicht): Hat die freie Erfindung einen Betriebsbezug (sie fällt in den Arbeitsbereich des Betriebes), ist der Arbeitnehmer verpflichtet, dem Arbeitgeber ein nicht-ausschließliches Nutzungsrecht (einfache Lizenz) in Textform anzubieten, bevor er sie einem Dritten anbietet. Zweck: Ausgleich zwischen Arbeitnehmerinteressen (volle Rechtsinhaberschaft) und Betriebsinteressen (Zugang zu betriebsrelevanten Erfindungen).",
    // FIX (verifiziert): § 18-Tatbestand entkoppelt vom Betriebsbezug (§ 18 gilt für
    // JEDE freie Erfindung, Ausnahme § 18 Abs. 3); Betriebsbezug klargestellt als
    // § 19-Trigger; "schriftlich" → "in Textform (§ 126b BGB)" in § 18 und § 19.
    sourceIds: ["arbnerfg18", "arbnerfg19", "bgb126b"],
    sources: [
      {
        id: "arbnerfg18",
        label: "§ 18 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__18.html",
      },
      {
        id: "arbnerfg19",
        label: "§ 19 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__19.html",
      },
      {
        id: "bgb126b",
        label: "§ 126b BGB",
        url: "https://www.gesetze-im-internet.de/bgb/__126b.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["arbnerf", "freie-erfindung", "anbietungspflicht"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-006",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "ArbnErfG §§ 28, 34, 37 — Schiedsstelle DPMA",
    title: "Schiedsstelle beim DPMA (§§ 28, 34, 37 ArbnErfG)",
    prompt:
      "Welche Funktion hat die Schiedsstelle beim DPMA, und ist ihre Anrufung vor einer Klage zwingend?",
    options: [],
    answers: [],
    explanation:
      "§ 28 Abs. 1 ArbnErfG: Beim Deutschen Patent- und Markenamt wird eine Schiedsstelle gebildet, die bei Streitigkeiten zwischen Arbeitgeber und Arbeitnehmer über Rechte aus Erfindungen und technischen Verbesserungsvorschlägen tätig wird. § 37 Abs. 1 ArbnErfG: Vor der Erhebung einer Klage in bürgerlichen Rechtsstreitigkeiten über Gegenstände des ArbnErfG muss die Schiedsstelle angerufen werden — die vorherige Anrufung ist zwingend (Zulässigkeitsvoraussetzung für die Klage). Ausnahmen regelt § 37 Abs. 2 (u.a. Ausscheiden aus dem Betrieb, 6 Monate ohne Vorschlag). § 34 ArbnErfG: Die Schiedsstelle unterbreitet einen Einigungsvorschlag. Widerspricht keine Partei binnen eines Monats schriftlich, gilt der Vorschlag als angenommen. Widerspricht eine Partei, gilt er als nicht gemacht; die annehmende Partei kann ihn im Klageverfahren als Angebot verwenden.",
    // FIX (verifiziert): § 29 (Besetzung) → § 37 (Klagevoraussetzung); § 30 (Besetzung)
    // → § 34 (Einigungsvorschlag); Annahmefiktion (1-Monat-Widerspruchsfrist) ergänzt.
    sourceIds: ["arbnerfg28", "arbnerfg34", "arbnerfg37"],
    sources: [
      {
        id: "arbnerfg28",
        label: "§ 28 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__28.html",
      },
      {
        id: "arbnerfg34",
        label: "§ 34 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__34.html",
      },
      {
        id: "arbnerfg37",
        label: "§ 37 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__37.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Verfahren",
    skillTags: ["arbnerf", "schiedsstelle", "dpma"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-007",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "ArbnErfG § 42 — Hochschulerfindungen",
    title: "Sonderregeln für Hochschullehrer-Erfindungen (§ 42 ArbnErfG)",
    prompt:
      "Welche Sonderregeln gelten nach § 42 ArbnErfG für Erfindungen von Arbeitnehmern an Hochschulen, insbesondere im Hinblick auf die Publikationsfreiheit?",
    options: [],
    answers: [],
    explanation:
      "§ 42 ArbnErfG (gegliedert in Nummern, nicht Absätze): § 42 Nr. 1 ArbnErfG enthält das Publikations- und Offenbarungsrecht: Beabsichtigt ein Hochschulbeschäftigter, eine Diensterfindung zu offenbaren (z.B. in einer Fachzeitschrift zu veröffentlichen), hat er dies der Hochschule rechtzeitig, in der Regel zwei Monate zuvor, anzuzeigen. Die Hochschule kann innerhalb dieser Frist die Inanspruchnahme erklären, um vor der Offenbarung eine Patentanmeldung einzureichen. § 42 Nr. 1 gewährt dem Hochschulbeschäftigten damit zugleich ein Recht zur Offenbarung (Publikationsfreiheit). § 42 Nr. 2 gibt zusätzlich das negative Publikationsrecht: der Erfinder kann verlangen, dass die Erfindung nicht offenbart wird. Dieses Spannungsfeld zwischen Publikationsfreiheit und Patentschutz ist prüfungsrelevant.",
    // FIX (verifiziert): "§ 42 Abs. 1/2/3" → "§ 42 Nr. 1/Nr. 2" (§ 42 ist in Nummern
    // gegliedert); Charakterisierung als Offenbarungs-/Publikationsrecht geschärft;
    // 2-Monats-Frist als AN-Anzeigepflicht (nicht AG-Frist) klargestellt.
    sourceIds: ["arbnerfg42"],
    sources: [
      {
        id: "arbnerfg42",
        label: "§ 42 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__42.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Sonderregeln",
    skillTags: ["arbnerf", "hochschulerfindung", "publikationsfreiheit"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-008",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "ArbnErfG § 7 — Wirkung der Inanspruchnahme",
    title: "Rechtsübergang durch Inanspruchnahme (§ 7 ArbnErfG)",
    prompt:
      "Was bewirkt die Inanspruchnahme einer Diensterfindung durch den Arbeitgeber nach § 7 ArbnErfG?",
    options: [],
    answers: [],
    explanation:
      "§ 7 Abs. 1 ArbnErfG: Mit der Inanspruchnahme (ob durch ausdrückliche Erklärung oder durch Fiktionswirkung nach § 6 Abs. 2 ArbnErfG) gehen alle Rechte an der Diensterfindung auf den Arbeitgeber über. Das umfasst: das Recht auf das Patent (§ 6 PatG analog), das Recht zur Anmeldung des Patents im Inland (§ 13 ArbnErfG) und im Ausland, sowie alle sonstigen Nutzungsrechte. Der Arbeitnehmer verliert alle Verfügungsrechte an der Erfindung, behält jedoch das unveräußerliche Erfinderpersönlichkeitsrecht (Erfindernennung, § 63 PatG). Als Gegenleistung entsteht der Vergütungsanspruch nach §§ 9 ff. ArbnErfG. § 7 Abs. 2 ArbnErfG: Verfügungen, die der Arbeitnehmer über eine Diensterfindung vor der Inanspruchnahme getroffen hat (z.B. Übertragung, Lizenzierung an Dritte), sind dem Arbeitgeber gegenüber unwirksam, soweit seine Rechte beeinträchtigt werden (Unwirksamkeit von Vorausverfügungen).",
    // FIX (verifiziert): § 7 Abs. 2 "Mitteilungspflicht" gestrichen; stattdessen
    // korrekte Regelung: Unwirksamkeit von Vorausverfügungen des AN gegenüber dem AG.
    sourceIds: ["arbnerfg7"],
    sources: [
      {
        id: "arbnerfg7",
        label: "§ 7 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__7.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Rechtsfolge",
    skillTags: ["arbnerf", "inanspruchnahme", "rechtsuebergang"],
    reviewStatus: "reviewed",
  },

  // ─── MC SINGLE da-arbnerf-009 … da-arbnerf-028 ─────────────────────────────

  {
    id: "da-arbnerf-009",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "ArbnErfG § 2 — Erfindungsbegriff",
    title: "Welche Schöpfungen fallen unter § 2 ArbnErfG?",
    prompt:
      "§ 2 ArbnErfG bestimmt den Geltungsbereich des Gesetzes. Welche Schöpfungen fallen unter die Regelungen des ArbnErfG?",
    options: [
      "Nur Erfindungen, die tatsächlich zum Patent angemeldet wurden",
      "Erfindungen (patentfähig oder gebrauchsmusterfähig) und technische Verbesserungsvorschläge, die aus der Tätigkeit im Betrieb hervorgehen",
      "Urheberrechtlich schutzfähige Werke, die ein Arbeitnehmer im Dienst erstellt",
      "Alle geistigen Leistungen eines Arbeitnehmers, die im Betrieb entstanden sind",
    ],
    answers: [1],
    explanation:
      "§ 2 ArbnErfG: Das Gesetz gilt für Erfindungen und technische Verbesserungsvorschläge von Arbeitnehmern im privaten und öffentlichen Dienst. 'Erfindungen' im Sinne des ArbnErfG sind Erfindungen, die patent- oder gebrauchsmusterschutzfähig sind (§ 2 Abs. 1 ArbnErfG) — eine tatsächliche Anmeldung oder Erteilung ist nicht Voraussetzung. Urheberrechtliche Werke (z.B. Software als Literaturwerk) fallen nicht unter das ArbnErfG (dafür gelten §§ 69a ff. UrhG). Bloße Ideen oder Informationen, die nicht patentfähig sind, unterfallen nur dann dem ArbnErfG, wenn sie als technische Verbesserungsvorschläge qualifizieren.",
    sourceIds: ["arbnerfg2"],
    sources: [
      {
        id: "arbnerfg2",
        label: "§ 2 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__2.html",
      },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["arbnerf", "geltungsbereich"],
    // REVIEW (question-reviewer): REVIEWED — Option B korrekt: § 2 ArbnErfG erfasst
    // patent-/gebrauchsmusterfähige Erfindungen und technische Verbesserungsvorschläge,
    // ohne Anmelde-/Erteilungserfordernis; UrhG-Werke fallen nicht darunter. genau 4
    // Optionen, 1 richtig, Distraktoren plausibel-falsch. Freigabe.
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-010",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "ArbnErfG § 4 — Diensterfindung vs. freie Erfindung (Fall)",
    title: "Fall: Abgrenzung Diensterfindung / freie Erfindung",
    prompt:
      "Ingenieur I arbeitet in der Entwicklungsabteilung eines Motorenherstellers und entwickelt in seiner Freizeit zu Hause eine neue Fahrradklingel. Die Entwicklung erfolgt ohne Betriebsmittel des Arbeitgebers; sein Aufgabenbereich umfasst ausschließlich Verbrennungsmotoren. Wie ist die Erfindung einzuordnen?",
    options: [
      "Diensterfindung nach § 4 Abs. 2 Var. 1 ArbnErfG, weil I während seines Arbeitsverhältnisses tätig war",
      "Diensterfindung nach § 4 Abs. 2 Var. 2 ArbnErfG, weil I auf Erfahrungen des Betriebs zurückgegriffen hat",
      "Freie Erfindung nach § 4 Abs. 3 ArbnErfG, weil weder eine betriebliche Aufgabe noch betriebliche Ressourcen die Erfindung geprägt haben",
      "Technischer Verbesserungsvorschlag, weil die Erfindung außerhalb des Aufgabenbereichs entstanden ist",
    ],
    answers: [2],
    explanation:
      "§ 4 Abs. 2 ArbnErfG: Eine Diensterfindung liegt vor, wenn die Erfindung entweder aus der obliegenden Tätigkeit entstanden ist (Var. 1: Aufgabenbereich = Verbrennungsmotoren — nicht erfüllt) oder maßgeblich auf Betriebserfahrungen beruht (Var. 2: keine Betriebsmittel, keine betriebliche Erfahrung für Fahrradklingeln genutzt — nicht erfüllt). Da keine der Varianten greift, ist die Fahrradklingel-Erfindung eine freie Erfindung nach § 4 Abs. 3 ArbnErfG. Der Arbeitnehmer kann über sie frei verfügen. Hinweis: Die Mitteilungspflicht nach § 18 ArbnErfG trifft den Arbeitnehmer bei jeder freien Erfindung (Ausnahme: offensichtlich nicht verwendbar, § 18 Abs. 3); die Anbietungspflicht nach § 19 ArbnErfG (Angebot einer einfachen Lizenz in Textform) greift nur dann, wenn die freie Erfindung in den Arbeitsbereich des Betriebes fällt — was hier mangels thematischer Überschneidung zweifelhaft ist.",
    sourceIds: ["arbnerfg4", "arbnerfg18", "arbnerfg19"],
    sources: [
      {
        id: "arbnerfg4",
        label: "§ 4 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__4.html",
      },
      {
        id: "arbnerfg18",
        label: "§ 18 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__18.html",
      },
      {
        id: "arbnerfg19",
        label: "§ 19 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__19.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["arbnerf", "diensterfindung", "freie-erfindung"],
    // FIX (verifiziert): § 18-Schlusssatz entkoppelt vom Betriebsbezug (§ 18 = jede
    // freie Erfindung; Betriebsbezug = § 19-Trigger); "Textform" statt "schriftlich"
    // in § 19-Hinweis; sources um arbnerfg18/arbnerfg19 ergänzt.
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-011",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "ArbnErfG § 4 — Erfahrungserfindung (Fall)",
    title: "Fall: Diensterfindung kraft betrieblicher Erfahrungen (§ 4 Abs. 2 Var. 2)",
    prompt:
      "Chemikerin C arbeitet bei Pharmaunternehmen P im Bereich Syntheseforschung. In ihrer Freizeit entwickelt sie zu Hause — ohne Laborgeräte des Arbeitgebers — eine neue Syntheseroute für einen Wirkstoff, der nichts mit ihren Dienstaufgaben zu tun hat. Grundlage ist jedoch eine Methode, die sie ausschließlich in ihrer Tätigkeit bei P gelernt hat. Wie ist die Erfindung einzuordnen?",
    options: [
      "Freie Erfindung, weil die Entwicklung in der Freizeit und ohne Betriebsmittel erfolgte",
      "Diensterfindung nach § 4 Abs. 2 Var. 2 ArbnErfG, weil die Erfindung maßgeblich auf betrieblichen Erfahrungen (der bei P gelernten Methode) beruht",
      "Freie Erfindung, weil der Wirkstoff nichts mit den Dienstaufgaben zu tun hat",
      "Technischer Verbesserungsvorschlag, weil C die Methode des Betriebs nur in modifizierter Form einsetzt",
    ],
    answers: [1],
    explanation:
      "§ 4 Abs. 2 Var. 2 ArbnErfG: Die Erfahrungserfindung liegt vor, wenn die Erfindung 'maßgeblich auf Erfahrungen oder Arbeiten des Betriebes beruht'. 'Maßgeblich' bedeutet, dass die betrieblichen Erfahrungen einen wesentlichen Beitrag zum Erfindungsgedanken geleistet haben. Dass die Synthesemethode ausschließlich im Betrieb erlernt und angewendet wurde, begründet eine solche Maßgeblichkeit. Freizeitentwicklung und Nicht-Nutzung von Betriebsmitteln schließen Var. 2 nicht aus — die Var. 2 setzt gerade kein Tätigwerden 'im' Betrieb voraus. Die Erfindung ist daher als Diensterfindung einzuordnen; C muss sie melden (§ 5 ArbnErfG).",
    sourceIds: ["arbnerfg4"],
    sources: [
      {
        id: "arbnerfg4",
        label: "§ 4 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__4.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["arbnerf", "diensterfindung", "erfahrungserfindung"],
    // REVIEW (question-reviewer): REVIEWED — Option B korrekt: Erfahrungserfindung
    // nach § 4 Abs. 2 Var. 2 ("maßgeblich auf Erfahrungen oder Arbeiten des Betriebes
    // beruht"); Freizeit/keine Betriebsmittel schließen Var. 2 nicht aus. Subsumtion
    // sauber, Distraktoren plausibel-falsch, Meldepflicht-Hinweis (§ 5) korrekt.
    // Freigabe.
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-012",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "ArbnErfG § 5 — Meldepflicht: Form und Frist",
    title: "Meldepflicht: Was ist 'gesondert in Textform'?",
    prompt:
      "Der Arbeitnehmer meldet seine Diensterfindung mündlich in einer Teambesprechung und verweist dabei auf einen beigefügten technischen Bericht. Ist diese Meldung nach § 5 Abs. 1 ArbnErfG wirksam?",
    options: [
      "Ja — mündliche Meldung mit schriftlichem Bericht erfüllt das Textformerfordernis.",
      "Ja — wenn der Arbeitgeber die Erfindung zur Kenntnis genommen hat, ist die Meldung in jedem Fall wirksam.",
      "Nein — § 5 Abs. 1 ArbnErfG verlangt eine gesonderte Meldung in Textform (§ 126b BGB); eine mündliche Meldung in einer Besprechung genügt nicht.",
      "Nein — eine Meldung muss stets notariell beurkundet werden.",
    ],
    answers: [2],
    explanation:
      "§ 5 Abs. 1 ArbnErfG: Der Arbeitnehmer ist verpflichtet, eine Diensterfindung dem Arbeitgeber unverzüglich gesondert in Textform (§ 126b BGB) zu melden. Das Merkmal 'gesondert' bedeutet, dass die Meldung als solche erkennbar sein und nicht mit anderen Informationen (z.B. Berichten, E-Mail-Konversationen) vermengt sein darf. Textform (§ 126b BGB) erfordert eine lesbare Erklärung auf einem dauerhaften Datenträger, die die Person des Erklärenden erkennen lässt — eine eigenhändige Unterschrift ist gerade NICHT erforderlich (E-Mail genügt). Eine mündliche Mitteilung in einer Besprechung erfüllt die Textform nicht, auch nicht wenn ein schriftlicher Bericht beigezogen wird. Eine nicht ordnungsgemäße Meldung setzt die 4-Monats-Frist des § 6 Abs. 2 ArbnErfG nicht in Gang.",
    sourceIds: ["arbnerfg5", "bgb126b"],
    sources: [
      {
        id: "arbnerfg5",
        label: "§ 5 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__5.html",
      },
      {
        id: "bgb126b",
        label: "§ 126b BGB",
        url: "https://www.gesetze-im-internet.de/bgb/__126b.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Formerfordernis",
    skillTags: ["arbnerf", "meldepflicht", "textform"],
    // FIX (verifiziert): Option A/C + Erklärung: "schriftlich / § 126 BGB analog /
    // eigenhändige Unterzeichnung" → Textform (§ 126b BGB); Textform-Definition
    // (kein Unterschriftserfordernis) ergänzt; bgb126b als source ergänzt.
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-013",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "ArbnErfG § 6 — Fiktive Inanspruchnahme: Rechtslage nach 2009",
    title: "Fiktive Inanspruchnahme — Systemwechsel seit 2009",
    prompt:
      "Was ist der entscheidende Unterschied der Inanspruchnahme nach § 6 ArbnErfG in der Fassung seit der Reform 2009 gegenüber dem früheren Recht?",
    options: [
      "Seit 2009 muss der Arbeitgeber die Erfindung ausdrücklich in Anspruch nehmen; Schweigen gilt als Freigabe.",
      "Seit 2009 gilt Schweigen des Arbeitgebers nach Ablauf von 4 Monaten als Inanspruchnahme (fiktive Inanspruchnahme); aktive Erklärung ist nicht mehr erforderlich.",
      "Seit 2009 ist die Inanspruchnahmefrist von 4 auf 6 Monate verlängert worden.",
      "Seit 2009 kann der Arbeitgeber eine beschränkte Inanspruchnahme erklären und dem Arbeitnehmer Teilrechte belassen.",
    ],
    answers: [1],
    explanation:
      "§ 6 Abs. 2 ArbnErfG (seit der PatG-Reform 2009, in Kraft seit 01.10.2009): Die Diensterfindung gilt als in Anspruch genommen, wenn der Arbeitgeber sie nicht innerhalb von vier Monaten nach Eingang der ordnungsgemäßen Meldung (§ 5 ArbnErfG) durch Erklärung in Textform (§ 126b BGB) freigibt. Schweigen = Inanspruchnahme. Im früheren Recht (vor 2009) war es umgekehrt: Der Arbeitgeber musste die Erfindung durch ausdrückliche Erklärung in Anspruch nehmen (Schweigen = keine Inanspruchnahme). Option D beschreibt eine Rechtslage, die nicht im Gesetz steht — eine 'beschränkte Inanspruchnahme' nach altem Recht ist entfallen; seit 2009 gibt es nur die volle Inanspruchnahme oder Freigabe.",
    // FIX (verifiziert): "durch schriftliche Erklärung" → "durch Erklärung in Textform
    // (§ 126b BGB)".
    sourceIds: ["arbnerfg6", "bgb126b"],
    sources: [
      {
        id: "arbnerfg6",
        label: "§ 6 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__6.html",
      },
      {
        id: "bgb126b",
        label: "§ 126b BGB",
        url: "https://www.gesetze-im-internet.de/bgb/__126b.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["arbnerf", "inanspruchnahme", "4-monats-frist"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-014",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "ArbnErfG § 6 — Fristbeginn (Fall)",
    title: "Fall: Wann beginnt die 4-Monats-Frist des § 6 Abs. 2 ArbnErfG?",
    prompt:
      "Arbeitnehmerin A reicht am 1. März eine schriftliche Meldung ihrer Diensterfindung ein. Der Arbeitgeber stellt fest, dass die Meldung die Angaben nach § 5 Abs. 2 ArbnErfG (technische Beschreibung, Entstehung) nicht vollständig enthält. Er fordert A am 5. März zur Ergänzung auf; A reicht die ergänzte Meldung am 20. März ein. Wann endet die 4-Monats-Frist des § 6 Abs. 2 ArbnErfG?",
    options: [
      "Am 1. Juli (4 Monate ab ursprünglicher Einreichung am 1. März)",
      "Am 5. Juli (4 Monate ab Aufforderung zur Ergänzung am 5. März)",
      "Am 20. Juli (4 Monate ab Eingang der ordnungsgemäßen ergänzten Meldung am 20. März)",
      "Am 1. April (1 Monat nach der ursprünglichen Meldung)",
    ],
    answers: [2],
    explanation:
      "§ 6 Abs. 2 ArbnErfG: Die 4-Monats-Frist beginnt mit dem Eingang der 'ordnungsgemäßen Meldung'. Eine Meldung ist erst dann ordnungsgemäß im Sinne von § 5 Abs. 2 ArbnErfG, wenn sie die Angaben über technische Aufgabe, Lösung und Zustandekommen enthält. Ist die ursprüngliche Meldung unvollständig und fordert der Arbeitgeber zur Ergänzung auf (§ 5 Abs. 3 ArbnErfG), beginnt die 4-Monats-Frist erst mit dem Eingang der ordnungsgemäß ergänzten Meldung — hier also am 20. März. Die Frist endet daher am 20. Juli. Eine unvollständige Meldung setzt die Frist nicht in Gang.",
    sourceIds: ["arbnerfg5", "arbnerfg6"],
    sources: [
      {
        id: "arbnerfg5",
        label: "§ 5 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__5.html",
      },
      {
        id: "arbnerfg6",
        label: "§ 6 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__6.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Fristberechnung",
    skillTags: ["arbnerf", "inanspruchnahme", "4-monats-frist", "meldepflicht"],
    // REVIEW (question-reviewer): REVIEWED — Option C (20. Juli) korrekt: AG hat hier
    // am 5. März fristgerecht (binnen 2 Monaten, § 5 Abs. 3 S. 2) zur Ergänzung
    // aufgefordert, daher greift die Heilungsfiktion NICHT und die 4-Monats-Frist
    // beginnt erst mit Eingang der ordnungsgemäßen Meldung am 20. März. Sauber
    // konstruiert und konsistent zur Heilungsfiktion-Konstellation in da-arbnerf-025
    // (dort beanstandet der AG gerade nicht). Distraktoren plausibel. Freigabe.
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-015",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "ArbnErfG § 6 — Fristversäumnis: Rechtsfolge",
    title: "Rechtsfolge des Fristversäumnisses bei § 6 Abs. 2 ArbnErfG",
    prompt:
      "Arbeitgeber AG versäumt die 4-Monats-Frist des § 6 Abs. 2 ArbnErfG und gibt die Diensterfindung nicht frei. Was ist die Rechtsfolge?",
    options: [
      "AG verliert das Recht zur Inanspruchnahme endgültig; die Erfindung wird zur freien Erfindung.",
      "Die Diensterfindung gilt kraft Gesetz als in Anspruch genommen; alle Rechte gehen auf AG über (§ 7 ArbnErfG).",
      "AG kann die Inanspruchnahme noch bis zu 6 Monate nach Fristablauf nachholen.",
      "AG muss die Inanspruchnahme durch eine notarielle Erklärung nachträglich formalisieren.",
    ],
    answers: [1],
    explanation:
      "§ 6 Abs. 2 ArbnErfG: Gibt der Arbeitgeber die Diensterfindung nicht innerhalb von vier Monaten nach ordnungsgemäßer Meldung in Textform frei, gilt die Erfindung als in Anspruch genommen — Inanspruchnahme tritt kraft Gesetzes ein (Fiktionswirkung). Die Rechtsfolge der Inanspruchnahme richtet sich nach § 7 ArbnErfG: Alle Rechte an der Diensterfindung gehen auf den Arbeitgeber über. Eine nachträgliche Nachholfrist gibt es nicht. Wenn der Arbeitgeber die Erfindung nicht verwerten will, müsste er — nach erfolgter Inanspruchnahme — die Schutzrechte nach § 16 ArbnErfG dem Arbeitnehmer anbieten (Anbietungspflicht bei Aufgabe).",
    sourceIds: ["arbnerfg6", "arbnerfg7"],
    sources: [
      {
        id: "arbnerfg6",
        label: "§ 6 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__6.html",
      },
      {
        id: "arbnerfg7",
        label: "§ 7 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__7.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Rechtsfolge",
    skillTags: ["arbnerf", "inanspruchnahme", "4-monats-frist", "rechtsuebergang"],
    // REVIEW (question-reviewer): REVIEWED — Option B korrekt: bei Fristversäumnis
    // (keine fristgerechte Freigabe) tritt die fiktive Inanspruchnahme kraft Gesetzes
    // ein (§ 6 Abs. 2), Rechtsübergang nach § 7; keine Nachholfrist. § 16-Hinweis
    // (Anbietungspflicht bei späterer Aufgabe) zutreffend. Distraktoren plausibel.
    // Freigabe.
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-016",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "ArbnErfG § 13 — Anmeldepflicht",
    title: "Inländische Schutzrechtsanmeldung (§ 13 ArbnErfG)",
    prompt:
      "Welche Pflicht entsteht für den Arbeitgeber nach § 13 ArbnErfG, wenn er eine Diensterfindung in Anspruch genommen hat?",
    options: [
      "Der Arbeitgeber muss die Erfindung sofort gewerblich verwerten.",
      "Der Arbeitgeber ist verpflichtet, die Diensterfindung unverzüglich zum Patent oder Gebrauchsmuster im Inland anzumelden.",
      "Der Arbeitgeber hat die Wahl, ob er ein Patent oder eine andere Form des Schutzes wählt, ohne Anmeldepflicht.",
      "Der Arbeitgeber muss die Erfindung innerhalb von sechs Monaten im Inland und Ausland anmelden.",
    ],
    answers: [1],
    explanation:
      "§ 13 Abs. 1 ArbnErfG: Der Arbeitgeber ist verpflichtet, eine in Anspruch genommene Diensterfindung unverzüglich zum Patent oder zum Gebrauchsmuster im Inland anzumelden. Diese Anmeldepflicht dient dem Schutz des Arbeitnehmers, damit sein Vergütungsanspruch auf einer gesicherten Schutzrechtsbasis entsteht und bewertet werden kann. § 13 Abs. 2 ArbnErfG: Wenn der Arbeitgeber glaubt, dass kein Schutzrecht erteilt werden wird, kann er — unter bestimmten Voraussetzungen — von der Anmeldung absehen; er muss dann die Erfindung nach § 16 ArbnErfG dem Arbeitnehmer anbieten. Eine pauschale 6-Monats-Frist für In- und Ausland existiert so nicht.",
    sourceIds: ["arbnerfg13"],
    sources: [
      {
        id: "arbnerfg13",
        label: "§ 13 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__13.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Pflicht",
    skillTags: ["arbnerf", "anmeldepflicht", "schutzrecht"],
    // REVIEW (question-reviewer): REVIEWED — Option B korrekt: § 13 Abs. 1 ArbnErfG
    // verpflichtet den AG, die in Anspruch genommene Diensterfindung unverzüglich im
    // Inland zum Patent oder Gebrauchsmuster anzumelden; § 13 Abs. 2 (Absehen unter
    // Voraussetzungen, dann § 16-Anbietung) zutreffend skizziert. Distraktor D
    // (pauschale 6-Monats-Frist In-/Ausland) sauber falsch. Freigabe.
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-017",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "ArbnErfG § 16 — Aufgabe des Schutzrechts / Anbietungspflicht (Fall)",
    title: "Fall: Anbietungspflicht bei Aufgabe des Schutzrechts (§ 16 ArbnErfG)",
    prompt:
      "Arbeitgeber AG hat ein Patent auf eine Diensterfindung von Arbeitnehmer A erhalten. Drei Jahre nach Erteilung entscheidet AG, das Patent nicht mehr aufrechtzuerhalten und die Jahresgebühren nicht zu zahlen. Welche Pflicht hat AG gegenüber A?",
    options: [
      "Keine — AG kann ein erteiltes Patent jederzeit ohne Weiteres aufgeben.",
      "AG muss A das Patent kostenlos übertragen.",
      "AG muss A vor Aufgabe des Schutzrechts anbieten, ihm das Schutzrecht zu übertragen, damit A es ggf. auf eigene Kosten weiterführen kann (§ 16 Abs. 1 ArbnErfG).",
      "AG muss die Jahresgebühren stets für die gesamte Patentlaufzeit zahlen.",
    ],
    answers: [2],
    explanation:
      "§ 16 Abs. 1 ArbnErfG: Will der Arbeitgeber ein Schutzrecht auf eine Diensterfindung aufgeben, so hat er dem Arbeitnehmer dies rechtzeitig mitzuteilen und ihm anzubieten, das Schutzrecht auf ihn zu übertragen. Diese Anbietungspflicht soll verhindern, dass der Arbeitgeber das Schutzrecht einfach verfallen lässt, ohne dem Erfinder die Möglichkeit zu geben, es zu übernehmen und selbst zu nutzen oder zu lizenzieren. Eine Pflicht zur kostenlosen Übertragung besteht nicht — Einzelheiten (Kosten, Bedingungen) richten sich nach den Umständen des Einzelfalls. Eine Pflicht zur Gebührenzahlung für die gesamte Laufzeit besteht nicht.",
    sourceIds: ["arbnerfg16"],
    sources: [
      {
        id: "arbnerfg16",
        label: "§ 16 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__16.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Pflicht",
    skillTags: ["arbnerf", "anbietungspflicht", "schutzrecht"],
    // REVIEW (question-reviewer): REVIEWED — Option C korrekt: § 16 Abs. 1 ArbnErfG —
    // will der AG das Schutzrecht aufgeben (hier: Jahresgebühren nicht zahlen), muss er
    // es zuvor dem AN rechtzeitig mitteilen und die Übertragung anbieten; keine Pflicht
    // zur kostenlosen Übertragung oder zur Gebührenzahlung über die gesamte Laufzeit.
    // Distraktoren sauber falsch. Freigabe.
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-018",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "ArbnErfG § 9 — Vergütung: Lizenzanalogie",
    title: "Lizenzanalogie als Vergütungsmethode",
    prompt:
      "Die Vergütungsrichtlinien des ArbnErfG sehen als häufigste Methode zur Berechnung der Arbeitnehmer-Vergütung die Lizenzanalogie vor. Was bedeutet das konkret?",
    options: [
      "Der Arbeitnehmer erhält eine feste Einmalzahlung unabhängig vom Umsatz des Arbeitgebers.",
      "Die Vergütung wird berechnet, als ob der Arbeitgeber von einem freien Erfinder eine Lizenz an der Erfindung erworben hätte — der hypothetische Lizenzsatz wird auf den relevanten Umsatz angewendet.",
      "Die Vergütung orientiert sich ausschließlich an den Jahresgebühren für das Patent.",
      "Die Vergütung wird nach dem Marktpreis berechnet, den ein Dritter für die Erfindung zahlen würde.",
    ],
    answers: [1],
    explanation:
      "§ 9 Abs. 2 ArbnErfG i.V.m. den Vergütungsrichtlinien des BMJV: Bei der Lizenzanalogie wird gefragt, welchen Lizenzsatz ein vernünftiger Lizenznehmer und Lizenzgeber in einer vergleichbaren Situation vereinbart hätten (fiktive Lizenz). Dieser Lizenzsatz (z.B. 3–5 % des Nettoumsatzes mit erfindungsgemäßen Produkten) wird auf den tatsächlich erzielten Umsatz des Arbeitgebers mit der Erfindung angewendet. Der so errechnete 'Lizenzwert' wird dann mit dem Anteilsfaktor des Arbeitnehmers (abhängig von Aufgaben und Betriebsanteil) multipliziert, um die individuelle Vergütung zu bestimmen. Die Methode ist die in der Praxis am häufigsten verwendete.",
    sourceIds: ["arbnerfg9", "arbnerfg11"],
    sources: [
      {
        id: "arbnerfg9",
        label: "§ 9 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__9.html",
      },
      {
        id: "arbnerfg11",
        label: "§ 11 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__11.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["arbnerf", "verguetung", "lizenzanalogie"],
    // REVIEW (question-reviewer): REVIEWED — Option B korrekt: Lizenzanalogie =
    // fiktiver Lizenzsatz (wie bei freiem Erfinder), angewendet auf den erfindungs-
    // gemäßen Umsatz, dann multipliziert mit dem Anteilsfaktor des AN. Erklärung mit
    // § 9 Abs. 2 i.V.m. Vergütungsrichtlinien sauber; Distraktoren plausibel-falsch.
    // Freigabe.
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-019",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "ArbnErfG §§ 18-19 — Freie Erfindungen (Fall)",
    title: "Fall: Anbietungspflicht bei freier Erfindung",
    prompt:
      "Maschinenbauingenieur M hat eine freie Erfindung (§ 4 Abs. 3 ArbnErfG) gemacht, die mit dem Tätigkeitsbereich seines Arbeitgebers AG in sachlichem Zusammenhang steht. M möchte die Erfindung sofort an den Konkurrenten K lizenzieren. Darf er das?",
    options: [
      "Ja — freie Erfindungen stehen ausschließlich dem Arbeitnehmer zu; er kann sie beliebig verwerten.",
      "Nein — M muss AG die Erfindung zunächst nach § 18 ArbnErfG mitteilen und ihm nach § 19 ArbnErfG ein nicht-ausschließliches Nutzungsrecht anbieten, bevor er sie Dritten anbietet.",
      "Nein — freie Erfindungen müssen immer zuerst dem Arbeitgeber vollständig abgetreten werden.",
      "Ja — § 19 ArbnErfG gilt nur für Diensterfindungen, nicht für freie Erfindungen.",
    ],
    answers: [1],
    explanation:
      "§ 18 ArbnErfG: Die Mitteilungspflicht trifft den Arbeitnehmer bei jeder freien Erfindung (Ausnahme: offensichtlich nicht verwendbar, § 18 Abs. 3 ArbnErfG) — unabhängig davon, ob ein Betriebsbezug besteht. M muss die Erfindung AG daher in Textform (§ 126b BGB) mitteilen. § 19 ArbnErfG: Da die freie Erfindung hier ausdrücklich in sachlichem Zusammenhang mit dem Tätigkeitsbereich von AG steht (Betriebsbezug = § 19-Trigger), muss M dem AG zusätzlich vor der Lizenzierung an Dritte ein nicht-ausschließliches Nutzungsrecht in Textform anbieten. M muss daher AG zuerst ein Angebot über eine einfache Lizenz unterbreiten. Erst wenn AG ablehnt (oder binnen angemessener Frist nicht reagiert), darf M die Erfindung an K lizenzieren. Eine Verpflichtung zur Abtretung besteht ausdrücklich nicht — nur das Angebot einer einfachen Lizenz.",
    sourceIds: ["arbnerfg18", "arbnerfg19", "bgb126b"],
    sources: [
      {
        id: "arbnerfg18",
        label: "§ 18 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__18.html",
      },
      {
        id: "arbnerfg19",
        label: "§ 19 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__19.html",
      },
      {
        id: "bgb126b",
        label: "§ 126b BGB",
        url: "https://www.gesetze-im-internet.de/bgb/__126b.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["arbnerf", "freie-erfindung", "anbietungspflicht"],
    // FIX (verifiziert): § 18-Erklärung entkoppelt vom Betriebsbezug (§ 18 = jede
    // freie Erfindung; Betriebsbezug = § 19-Trigger erklärt); "schriftlich" →
    // "in Textform (§ 126b BGB)"; bgb126b als source ergänzt.
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-020",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "ArbnErfG §§ 28, 37 — Schiedsstelle: Voraussetzung",
    title: "Ist die Schiedsstellen-Anrufung zwingend vor der Klage?",
    prompt:
      "Arbeitnehmer A ist mit der Vergütung seiner Diensterfindung unzufrieden und möchte sofort Klage erheben, ohne die Schiedsstelle beim DPMA anzurufen. Ist das zulässig?",
    options: [
      "Ja — die Schiedsstellen-Anrufung ist freiwillig; A kann direkt klagen.",
      "Ja — die Schiedsstelle ist nur für Streitigkeiten über die Einordnung einer Erfindung zuständig, nicht für Vergütungsstreitigkeiten.",
      "Nein — § 37 Abs. 1 ArbnErfG schreibt vor, dass die Schiedsstelle vor Erhebung einer Klage in bürgerlichen Rechtsstreitigkeiten über Gegenstände des ArbnErfG angerufen werden muss.",
      "Nein — A muss zunächst ein Schlichtungsverfahren bei der Handwerkskammer durchführen.",
    ],
    answers: [2],
    explanation:
      "§ 37 Abs. 1 ArbnErfG: Vor der Erhebung einer Klage in bürgerlichen Rechtsstreitigkeiten über Gegenstände des ArbnErfG — einschließlich Vergütungsstreitigkeiten — muss die Schiedsstelle beim DPMA angerufen werden. Dies ist eine prozessuale Zulässigkeitsvoraussetzung: Klagen, die ohne vorherige Anrufung der Schiedsstelle erhoben werden, sind unzulässig. Die Schiedsstelle ist für alle Streitigkeiten über Rechte aus Diensterfindungen und technischen Verbesserungsvorschlägen zuständig (§ 28 Abs. 1 ArbnErfG) — also auch für Vergütungsstreitigkeiten. Ausnahmen von der zwingenden Vorgängigkeit regelt § 37 Abs. 2 (z.B. Ausscheiden des AN aus dem Betrieb, 6 Monate seit Anrufung ohne Einigungsvorschlag).",
    // FIX (verifiziert): Option C + Erklärung: "§ 29 Abs. 1" → "§ 37 Abs. 1";
    // sourceIds: arbnerfg29 → arbnerfg37; § 37 Abs. 2-Ausnahmen ergänzt.
    sourceIds: ["arbnerfg28", "arbnerfg37"],
    sources: [
      {
        id: "arbnerfg28",
        label: "§ 28 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__28.html",
      },
      {
        id: "arbnerfg37",
        label: "§ 37 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__37.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Verfahren",
    skillTags: ["arbnerf", "schiedsstelle", "verfahren"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-021",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "ArbnErfG § 6 — Freigabe durch Arbeitgeber (Fall)",
    title: "Fall: Arbeitgeber gibt Erfindung innerhalb der 4-Monats-Frist frei",
    prompt:
      "Arbeitnehmer B meldet ordnungsgemäß eine Diensterfindung am 1. April. Arbeitgeber AG erklärt am 15. Mai in Textform die Freigabe der Erfindung nach § 6 Abs. 2 ArbnErfG. Was ist die Rechtsfolge?",
    options: [
      "Die Erfindung gilt dennoch als in Anspruch genommen, weil die 4-Monate-Frist noch nicht abgelaufen war.",
      "Die Erfindung wird zur freien Erfindung; B kann über sie frei verfügen, ohne Mitteilungs- oder Anbietungspflichten nach §§ 18-19 ArbnErfG.",
      "Die Freigabe ist nur wirksam, wenn auch B in Textform zustimmt.",
      "AG muss B für die Freigabe eine Entschädigung zahlen.",
    ],
    answers: [1],
    explanation:
      "§ 6 Abs. 2 ArbnErfG: Der Arbeitgeber kann die Diensterfindung durch Erklärung in Textform (§ 126b BGB) freigeben, bevor die 4-Monats-Frist abläuft. Mit der Freigabe verzichtet der Arbeitgeber auf alle Rechte an der Erfindung; die Erfindung wird zur freien Erfindung des Arbeitnehmers (§ 8 Abs. 1 ArbnErfG). Entscheidend: § 8 Abs. 2 ArbnErfG ordnet ausdrücklich an, dass die §§ 18 und 19 ArbnErfG für freigewordene Diensterfindungen nicht gelten — B unterliegt daher keiner Mitteilungs- oder Anbietungspflicht gegenüber AG. Eine Entschädigung für den Arbeitnehmer ist nicht vorgesehen.",
    // FIX (verifiziert): Prompt + Erklärung: "schriftlich" → "in Textform (§ 126b BGB)";
    // § 8 Abs. 2 als tragende Norm ergänzt (keine §§ 18/19-Pflichten kraft Gesetzes);
    // arbnerfg8 als source ergänzt.
    sourceIds: ["arbnerfg6", "arbnerfg8", "bgb126b"],
    sources: [
      {
        id: "arbnerfg6",
        label: "§ 6 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__6.html",
      },
      {
        id: "arbnerfg8",
        label: "§ 8 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__8.html",
      },
      {
        id: "bgb126b",
        label: "§ 126b BGB",
        url: "https://www.gesetze-im-internet.de/bgb/__126b.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Rechtsfolge",
    skillTags: ["arbnerf", "inanspruchnahme", "freigabe"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-022",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "ArbnErfG § 12 — Vergütungsanpassung",
    title: "Anpassung der Vergütung bei veränderten Umständen (§ 12 ArbnErfG)",
    prompt:
      "Nach § 12 ArbnErfG kann die Vergütung für eine Diensterfindung angepasst werden. Welche Situation rechtfertigt eine Anpassung?",
    options: [
      "Nur wenn der Arbeitnehmer eine neue Erfindung macht, die die alte ersetzt",
      "Wenn die Grundlagen der vereinbarten oder festgesetzten Vergütung sich wesentlich verändert haben, insbesondere weil der wirtschaftliche Wert der Erfindung sich erheblich anders entwickelt hat als erwartet",
      "Wenn der Arbeitgeber einen neuen Lizenzvertrag über die Erfindung abschließt",
      "Vergütungsanpassungen sind nach dem ArbnErfG grundsätzlich ausgeschlossen, sofern eine Vereinbarung getroffen wurde",
    ],
    answers: [1],
    explanation:
      "§ 12 ArbnErfG: Wenn sich die Grundlagen, die für die Bemessung der Vergütung maßgebend waren, wesentlich ändern, so haben Arbeitnehmer und Arbeitgeber eine Anpassung der Vergütungsvereinbarung zu verlangen. Dies gilt insbesondere, wenn sich der wirtschaftliche Wert der Erfindung — z.B. durch unerwarteten Markterfolg oder unerwarteten Misserfolg, durch neue Anwendungsgebiete oder durch Wechsel der Lizenzierungsstrategie des Arbeitgebers — erheblich anders entwickelt als bei Festsetzung der Vergütung angenommen. Die Anpassung ist kein einseitiger Anspruch auf Erhöhung, sondern kann auch eine Reduzierung der Vergütung bedeuten, wenn der Erfindungswert gesunken ist.",
    sourceIds: ["arbnerfg12"],
    sources: [
      {
        id: "arbnerfg12",
        label: "§ 12 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__12.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Rechtsfolge",
    skillTags: ["arbnerf", "verguetung", "anpassung"],
    // REVIEW (question-reviewer): REVIEWED — Option B korrekt: § 12 ArbnErfG erlaubt
    // beiden Parteien, eine Anpassung zu verlangen, wenn sich die für die Festsetzung/
    // Vereinbarung maßgebenden Umstände wesentlich geändert haben; Anpassung kann auch
    // Reduzierung sein (zutreffend hervorgehoben). Distraktor D (Anpassung
    // ausgeschlossen) sauber falsch. Freigabe.
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-023",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "ArbnErfG § 42 — Hochschulerfindungen (Fall)",
    title: "Fall: Publikationsfreiheit vs. Patentschutz an der Hochschule",
    prompt:
      "Hochschulprofessor P will eine Erfindung in einer Fachzeitschrift veröffentlichen. Er unterrichtet die Hochschule rechtzeitig von seiner Absicht (§ 42 Nr. 1 ArbnErfG). Die Hochschule möchte die Erfindung vor der Veröffentlichung patentieren. Was muss die Hochschule tun, um dem Neuheitsschaden vorzubeugen?",
    options: [
      "Die Hochschule muss P an der Veröffentlichung rechtlich hindern, bis das Patent erteilt ist.",
      "Die Hochschule muss innerhalb einer gesetzlich vorgesehenen Frist nach Unterrichtung durch P die Inanspruchnahme erklären, bevor P veröffentlicht.",
      "P darf sofort veröffentlichen; die Hochschule hat kein Recht auf Verzögerung.",
      "Die Hochschule kann den Neuheitsschaden durch eine interne Geheimhaltungsvereinbarung mit P vermeiden.",
    ],
    answers: [1],
    explanation:
      "§ 42 Nr. 1 ArbnErfG: Beabsichtigt ein Hochschulbeschäftigter, eine Erfindung zu offenbaren, hat er den Arbeitgeber (die Hochschule) rechtzeitig, in der Regel zwei Monate zuvor, zu unterrichten. Die Hochschule kann innerhalb dieser Frist die Inanspruchnahme der Erfindung erklären. Während der Anzeigefrist muss P mit der Veröffentlichung warten, um den Neuheitsschaden zu vermeiden. Erklärt die Hochschule fristgerecht die Inanspruchnahme, geht das Recht über (§ 7 ArbnErfG) und die Hochschule kann eine Patentanmeldung einreichen, bevor P veröffentlicht. Eine permanente Veröffentlichungssperre bis zur Patenterteilung ist nicht zulässig.",
    // FIX (verifiziert): "§ 42 Abs. 3" → "§ 42 Nr. 1" (§ 42 ist in Nummern gegliedert);
    // "schriftlich" aus dem Prompt entfernt (§ 42 Nr. 1 nennt keine Schriftform);
    // Formulierung auf Anzeigepflicht des Erfinders (2 Monate zuvor) präzisiert.
    sourceIds: ["arbnerfg42", "arbnerfg7"],
    sources: [
      {
        id: "arbnerfg42",
        label: "§ 42 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__42.html",
      },
      {
        id: "arbnerfg7",
        label: "§ 7 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__7.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["arbnerf", "hochschulerfindung", "publikationsfreiheit"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-024",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "ArbnErfG §§ 9, 11 — Vergütungsrichtlinien",
    title: "Rechtsnatur der Vergütungsrichtlinien (§ 11 ArbnErfG)",
    prompt:
      "Welchen Rechtsstatus haben die 'Richtlinien für die Vergütung von Arbeitnehmererfindungen im privaten Dienst' nach § 11 ArbnErfG?",
    options: [
      "Sie sind zwingendes Gesetzesrecht und binden Arbeitgeber und Arbeitnehmer absolut.",
      "Sie sind unverbindliche Empfehlungen ohne jede Rechtswirkung.",
      "Sie sind keine zwingenden Rechtsnormen, entfalten aber Indizwirkung: Richtlinien-konforme Vergütungen gelten als angemessen; abweichende Vergütungen sind begründungsbedürftig.",
      "Sie sind nur für öffentlich-rechtliche Arbeitgeber verbindlich.",
    ],
    answers: [2],
    explanation:
      "§ 11 ArbnErfG: Das Bundesministerium der Justiz erlässt Richtlinien für die Vergütung von Arbeitnehmererfindungen. Diese Richtlinien sind keine zwingenden Rechtsnormen, entfalten aber Indizwirkung: Eine Vergütung, die gemäß den Richtlinien berechnet wurde, gilt in der Praxis als angemessen im Sinne des § 9 ArbnErfG; eine davon abweichende Vergütung ist begründungsbedürftig. Die Richtlinien enthalten Methoden (Lizenzanalogie, Schätzung usw.), Anteilsfaktoren und Beispielsätze. Sie gelten für private Arbeitgeber; für den öffentlichen Dienst gibt es gesonderte Regelungen.",
    sourceIds: ["arbnerfg9", "arbnerfg11"],
    sources: [
      {
        id: "arbnerfg9",
        label: "§ 9 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__9.html",
      },
      {
        id: "arbnerfg11",
        label: "§ 11 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__11.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Rechtsnatur",
    skillTags: ["arbnerf", "verguetung", "vergütungsrichtlinien"],
    // FIX (verifiziert): Option C-Text: "widerlegbare Vermutung … gilt als unangemessen"
    // → präzise Indizwirkungsformulierung (keine gesetzliche Vermutung i.S.d. § 292 ZPO).
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-025",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "ArbnErfG § 5 Abs. 3 — Heilungsfiktion (Fall)",
    title: "Fall: Heilungsfiktion bei unvollständiger Meldung — § 5 Abs. 3 S. 2 ArbnErfG",
    prompt:
      "Arbeitnehmer A meldet eine Diensterfindung gesondert in Textform, nennt aber nur die technische Aufgabe — Lösung und Zustandekommen fehlen (§ 5 Abs. 2 ArbnErfG). Arbeitgeber AG beanstandet die Meldung nicht und äußert sich zwei Monate lang nicht. Ist nach weiteren 4 Monaten (gerechnet ab dem ursprünglichen Eingang der Meldung) die fiktive Inanspruchnahme eingetreten?",
    options: [
      "Nein — die fiktive Inanspruchnahme setzt eine formal vollständige Meldung voraus; eine unvollständige Meldung kann die Frist nicht in Gang setzen.",
      "Nein — AG muss die Meldung zunächst ausdrücklich als ordnungsgemäß anerkennen, bevor die 4-Monats-Frist beginnt.",
      "Ja — da AG nicht binnen zwei Monaten in Textform erklärt hat, dass die Meldung ergänzungsbedürftig ist, gilt sie nach § 5 Abs. 3 Satz 2 ArbnErfG als ordnungsgemäß; die 4-Monats-Frist ist abgelaufen, und die fiktive Inanspruchnahme ist eingetreten.",
      "Nein — A ist in Verzug geraten und hat seinen Vergütungsanspruch verwirkt.",
    ],
    answers: [2],
    explanation:
      "§ 5 Abs. 3 Satz 2 ArbnErfG (Heilungsfiktion): Eine mangelhafte Meldung gilt als ordnungsgemäß, wenn der Arbeitgeber nicht binnen zwei Monaten in Textform (§ 126b BGB) erklärt, dass und in welcher Hinsicht die Meldung der Ergänzung bedarf. Da AG hier innerhalb von zwei Monaten schweigt, greift die Heilungsfiktion: Die unvollständige Meldung gilt rückwirkend als ordnungsgemäße Meldung i.S.d. § 5 Abs. 2 ArbnErfG. Ab dem ursprünglichen Eingang läuft daher die 4-Monats-Frist des § 6 Abs. 2 ArbnErfG. Da AG auch keine Freigabe in Textform erklärt, tritt nach Ablauf der 4 Monate die fiktive Inanspruchnahme ein. Abgrenzung zu da-arbnerf-014: Dort beanstandet AG fristgerecht (binnen 2 Monaten), sodass die Heilungsfiktion gerade nicht greift und die Frist erst ab der ordnungsgemäßen Ergänzung läuft.",
    // FIX (verifiziert): Item vollständig neu konzipiert. Sachverhalt: AG beanstandet
    // nicht binnen 2 Monaten → Heilungsfiktion § 5 Abs. 3 S. 2 → Meldung gilt als
    // ordnungsgemäß → 4-Monats-Frist läuft → fiktive Inanspruchnahme tritt ein.
    // Richtige Antwort: Option C (Index 2). Alle Distraktoren eindeutig falsch.
    sourceIds: ["arbnerfg5", "arbnerfg6", "bgb126b"],
    sources: [
      {
        id: "arbnerfg5",
        label: "§ 5 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__5.html",
      },
      {
        id: "arbnerfg6",
        label: "§ 6 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__6.html",
      },
      {
        id: "bgb126b",
        label: "§ 126b BGB",
        url: "https://www.gesetze-im-internet.de/bgb/__126b.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Fristberechnung",
    skillTags: ["arbnerf", "meldepflicht", "4-monats-frist", "inanspruchnahme"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-026",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "ArbnErfG § 14 — Auslandsanmeldung",
    title: "Auslandsanmeldung nach § 14 ArbnErfG",
    prompt:
      "Nach § 14 ArbnErfG kann der Arbeitgeber eine in Anspruch genommene Diensterfindung auch im Ausland zum Schutzrecht anmelden. Welche Pflichten treffen ihn dabei gegenüber dem Arbeitnehmer?",
    options: [
      "Der Arbeitgeber muss den Arbeitnehmer vor jeder Auslandsanmeldung um Zustimmung bitten.",
      "Der Arbeitgeber muss den Arbeitnehmer über beabsichtigte Auslandsanmeldungen informieren; der Arbeitnehmer kann für Länder, in denen der Arbeitgeber keine Anmeldung vornimmt, das Recht auf eigene Kosten erwerben.",
      "Auslandsanmeldungen sind im ArbnErfG nicht geregelt — es gilt ausschließlich ausländisches Recht.",
      "Der Arbeitgeber ist verpflichtet, die Erfindung in allen Ländern anzumelden, in denen die Erfindung wirtschaftlich bedeutsam ist.",
    ],
    answers: [1],
    explanation:
      "§ 14 ArbnErfG: Der Arbeitgeber ist berechtigt, die in Anspruch genommene Diensterfindung auch im Ausland zum Schutzrecht anzumelden. Er muss den Arbeitnehmer über beabsichtigte Auslandsanmeldungen informieren. Für Länder, in denen der Arbeitgeber keine Anmeldung vornimmt und keine Geheimhaltung wahrt, muss er dem Arbeitnehmer die Möglichkeit einräumen, auf eigene Kosten Schutz zu erlangen. Das ArbnErfG regelt den deutschen Schutzrahmen für grenzüberschreitende Fälle — eine Pflicht zur Anmeldung in allen wirtschaftlich bedeutsamen Ländern besteht nicht.",
    sourceIds: ["arbnerfg14"],
    sources: [
      {
        id: "arbnerfg14",
        label: "§ 14 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__14.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Pflicht",
    skillTags: ["arbnerf", "auslandsanmeldung", "schutzrecht"],
    // REVIEW (question-reviewer): REVIEWED — Option B trifft den Kern des § 14 ArbnErfG:
    // AG ist zur Auslandsanmeldung berechtigt, muss aber für Staaten, in denen er keine
    // Schutzrechte erwerben will, die Erfindung dem AN freigeben und ihm den Erwerb auf
    // eigene Kosten ermöglichen (§ 14 Abs. 2). Distraktoren (Zustimmungspflicht /
    // Anmeldepflicht in allen Ländern) sauber falsch. Freigabe.
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-027",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "ArbnErfG § 6 — 4-Monats-Frist: multiple Erfinder (Fall)",
    title: "Fall: Gemeinschaftliche Diensterfindung und Inanspruchnahme",
    prompt:
      "Arbeitnehmer A und B haben gemeinsam eine Diensterfindung gemacht. A meldet die Erfindung ordnungsgemäß am 1. Februar. B hat noch keine gesonderte Meldung abgegeben. Der Arbeitgeber erklärt am 30. Mai keine Freigabe. Gilt die fiktive Inanspruchnahme?",
    options: [
      "Nein — bei mehreren Erfindern muss jeder gesondert melden; die Frist beginnt erst, wenn alle ordnungsgemäß gemeldet haben.",
      "Ja — die Meldung von A genügt; die 4-Monats-Frist endete am 1. Juni (4 Monate nach A's Meldung) und der Arbeitgeber hat nicht fristgerecht freigegeben.",
      "Ja — aber nur für A's Anteil an der Erfindung; B's Anteil bleibt ungeklärt.",
      "Nein — gemeinschaftliche Erfindungen fallen nicht unter das ArbnErfG.",
    ],
    answers: [1],
    explanation:
      "§ 5 ArbnErfG i.V.m. § 6 ArbnErfG: Meldet einer der Miterfinder die gemeinschaftliche Diensterfindung ordnungsgemäß — mit Angaben zu Aufgabe, Lösung, Zustandekommen und Benennung aller Miterfinder — ist die Meldung für die gesamte Erfindung wirksam. Die 4-Monats-Frist beginnt mit Eingang dieser Meldung. Da A am 1. Februar gemeldet hat und die Frist am 1. Juni ablief, ohne dass der Arbeitgeber fristgerecht freigegeben hat, gilt die gesamte Erfindung (nicht nur A's Anteil) als in Anspruch genommen. Es gibt keine gesetzliche Pflicht, dass jeder Miterfinder eine eigene separate Meldung abgibt — die gemeinsame Meldung durch einen reicht aus.",
    sourceIds: ["arbnerfg5", "arbnerfg6"],
    sources: [
      {
        id: "arbnerfg5",
        label: "§ 5 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__5.html",
      },
      {
        id: "arbnerfg6",
        label: "§ 6 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__6.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["arbnerf", "inanspruchnahme", "4-monats-frist", "gemeinschaftserfindung"],
    // REVIEW (question-reviewer): REVIEWED — Option B tragfähig: Eine ordnungsgemäße
    // Meldung der gemeinschaftlichen Diensterfindung (die nach § 5 Abs. 2 die
    // Miterfinder benennt) setzt die 4-Monats-Frist für die GESAMTE Erfindung in Gang;
    // mangels fristgerechter Freigabe greift am 1. Juni die fiktive Inanspruchnahme.
    // Datumsrechnung (01.02. + 4 Mon. = 01.06.) korrekt; eine eigene Meldung jedes
    // Miterfinders ist nicht erforderlich (§ 5 Abs. 2 S. 3: gemeinsame Meldung zulässig).
    // Prämisse, dass A's "ordnungsgemäße" Meldung B als Miterfinder benennt, ist in der
    // Erklärung sauber gemacht. Freigabe.
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-028",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "ArbnErfG § 34 — Schiedsstelle: Einigungsvorschlag",
    title: "Wirkung des Einigungsvorschlags der Schiedsstelle (§ 34 ArbnErfG)",
    prompt:
      "Die Schiedsstelle beim DPMA unterbreitet einen Einigungsvorschlag nach § 34 ArbnErfG. Arbeitgeber AG nimmt ihn an; Arbeitnehmer A widerspricht schriftlich. Welche Rechtswirkung hat der Einigungsvorschlag?",
    options: [
      "Der Einigungsvorschlag ist für beide Parteien verbindlich wie ein Gerichtsurteil.",
      "Da A schriftlich widerspricht, gilt der Vorschlag als nicht gemacht; AG kann aber den angenommenen Vorschlag im Klageverfahren als Angebot verwenden, was Einfluss auf Kostenentscheidungen haben kann.",
      "Da A widerspricht, verliert AG seinen Anspruch, den Vorschlag zu verwenden.",
      "Der Einigungsvorschlag wird verbindlich, wenn ihn mindestens eine Partei annimmt.",
    ],
    answers: [1],
    explanation:
      "§ 34 ArbnErfG: Der Einigungsvorschlag der Schiedsstelle gilt als angenommen, wenn keine Partei binnen eines Monats nach Zustellung schriftlich widerspricht (Annahmefiktion). Widerspricht eine Partei fristgerecht schriftlich, gilt der Vorschlag als nicht gemacht — es besteht keine Bindungswirkung. Nimmt jedoch nur eine Partei (hier AG) den Vorschlag an, kann die annehmende Partei im anschließenden Klageverfahren auf den Vorschlag als Angebot zurückgreifen: A muss sich im Prozess daran messen lassen, was Einfluss auf Kostenentscheidungen haben kann. Der Vorschlag entfaltet mittelbare Wirkung als Verhandlungsgrundlage und Kostenrisikofaktor, nicht als verbindliche Entscheidung.",
    // FIX (verifiziert): Titel/Prompt/Erklärung/sourceIds: "§ 30" → "§ 34";
    // Annahmefiktion (1-Monat-Widerspruchsfrist, schriftlich) ergänzt.
    sourceIds: ["arbnerfg34"],
    sources: [
      {
        id: "arbnerfg34",
        label: "§ 34 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__34.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Verfahren",
    skillTags: ["arbnerf", "schiedsstelle", "einigungsvorschlag"],
    reviewStatus: "reviewed",
  },

  // ─── MC MULTI da-arbnerf-029 … da-arbnerf-032 ──────────────────────────────

  {
    id: "da-arbnerf-029",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "multi",
    topic: "ArbnErfG § 4 — Diensterfindung: Tatbestandsmerkmale (Fall)",
    title: "Fall: Welche Varianten des § 4 Abs. 2 ArbnErfG sind erfüllt? (Mehrfachauswahl)",
    prompt:
      "Softwareentwickler S arbeitet bei IT-Unternehmen U im Bereich Datenbankoptimierung. Er entwickelt in seiner Freizeit zu Hause einen neuen Algorithmus zur Datenkompression. Dabei nutzt er keine Betriebsmittel von U, greift aber maßgeblich auf ein Verfahren zurück, das er nur bei U in einem internen Schulungsprogramm erlernt hat, und das eng mit seiner täglichen Arbeit zusammenhängt. Welche der folgenden Aussagen sind zutreffend? (Mehrere Antworten möglich)",
    options: [
      "Var. 1 (§ 4 Abs. 2 Var. 1) ist NICHT erfüllt: Die Freizeit-Entwicklung eines Datenkompressionsalgorithmus fällt nicht unter die S obliegende Tätigkeit (Datenbankoptimierung ist ein anderer Bereich)",
      "Var. 2 (§ 4 Abs. 2 Var. 2) ist erfüllt: Die Erfindung beruht maßgeblich auf Betriebserfahrungen von U",
      "Weder Var. 1 noch Var. 2 ist erfüllt: Freizeitentwicklung ohne Betriebsmittel ist stets freie Erfindung",
      "Die Erfindung ist eine Diensterfindung, weil mindestens eine Variante des § 4 Abs. 2 erfüllt ist",
    ],
    answers: [1, 3],
    explanation:
      "§ 4 Abs. 2 ArbnErfG: Var. 1 setzt voraus, dass die Erfindung 'aus der dem Arbeitnehmer obliegenden Tätigkeit entstanden' ist. Da S im Bereich Datenbankoptimierung tätig ist und der neue Algorithmus der Datenkompression einem anderen Spezialgebiet entstammt, ist Var. 1 hier zu verneinen (Option A korrekt formuliert). Var. 2 ist dagegen klar erfüllt: Die maßgebliche Nutzung des ausschließlich beim Arbeitgeber U erlernten betriebsinternen Schulungsverfahrens belegt, dass die Erfindung 'maßgeblich auf Erfahrungen des Betriebes beruht'. Da Var. 2 erfüllt ist, liegt eine Diensterfindung vor (Antworten B und D sind zutreffend). Option C ist falsch — Freizeitentwicklung ohne Betriebsmittel schließt Var. 2 nicht aus; maßgeblich sind die genutzten Betriebserfahrungen.",
    sourceIds: ["arbnerfg4"],
    sources: [
      {
        id: "arbnerfg4",
        label: "§ 4 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__4.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["arbnerf", "diensterfindung", "erfahrungserfindung"],
    // FIX (verifiziert): Option A umformuliert: nicht mehr "Var. 1 ist erfüllt"
    // (ambig/vertretbar), sondern eindeutig "Var. 1 ist NICHT erfüllt" (klarer
    // Falschantwort-Distraktor vs. richtige Optionen B+D). Erklärung konsistent.
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-030",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "multi",
    topic: "ArbnErfG §§ 9-12 — Vergütung: Berechnungsfaktoren (Fall)",
    title: "Fall: Welche Faktoren mindern die Vergütungsquote? (Mehrfachauswahl)",
    prompt:
      "Ingenieur I hat eine Diensterfindung gemacht. Bei der Vergütungsberechnung nach § 9 ArbnErfG werden folgende Faktoren berücksichtigt: (1) I arbeitet in einer dedizierten F&E-Abteilung und wird gerade für Erfinden bezahlt; (2) der Arbeitgeber hat erhebliche Betriebsmittel (Labore, Materialien) zur Entwicklung bereitgestellt; (3) Vorarbeiten des Betriebs haben die Erfindung ermöglicht. Welche Aussagen zur Vergütungsquote sind zutreffend? (Mehrere Antworten möglich)",
    options: [
      "Faktor (1) reduziert die Vergütungsquote, weil Erfinden zu I's Arbeitsaufgaben gehört",
      "Faktoren (2) und (3) erhöhen den Anteil des Betriebs und reduzieren damit die Vergütungsquote des Arbeitnehmers",
      "Alle drei Faktoren sind irrelevant — entscheidend ist nur der wirtschaftliche Wert der Erfindung",
      "Faktor (1) allein schließt jeden Vergütungsanspruch aus",
    ],
    answers: [0, 1],
    explanation:
      "§ 9 Abs. 2 ArbnErfG: Die Vergütungsquote (Anteil des Arbeitnehmers am Erfindungswert) hängt von mehreren Faktoren ab. (1) Die Stellung im Betrieb: Arbeitnehmer, die für Erfinden eingestellt wurden (F&E-Mitarbeiter), erhalten typisch eine niedrigere Quote — ihre Aufgabe ist das Erfinden, daher ist der 'persönliche Erfindungsanteil' geringer (Antwort A korrekt). (2)+(3) Betriebsmittel und Vorarbeiten erhöhen den 'Anteil des Betriebs' und senken die Arbeitnehmerquote entsprechend (Antwort B korrekt). Option C ist falsch — der Erfindungswert bestimmt die Basis, aber die Quote ist separat zu bestimmen. Option D ist falsch — der Vergütungsanspruch bleibt immer bestehen (§ 9 Abs. 1 ArbnErfG ist zwingend), nur die Höhe variiert.",
    sourceIds: ["arbnerfg9"],
    sources: [
      {
        id: "arbnerfg9",
        label: "§ 9 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__9.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["arbnerf", "verguetung", "anteilsfaktor"],
    // REVIEW (question-reviewer): REVIEWED — Antwortschlüssel [A, B] korrekt: F&E-
    // Stellung senkt die Anteilsquote des AN, Betriebsmittel/Vorarbeiten erhöhen den
    // Betriebsanteil und senken die Quote (§ 9 Abs. 2). Option C (Faktoren irrelevant)
    // und D (Anspruch ausgeschlossen) sauber falsch — der Vergütungsanspruch ist
    // zwingend (§ 9 Abs. 1, unabdingbar § 22). 2 richtige Optionen, im multi-Bereich.
    // Freigabe.
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-031",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "multi",
    topic: "ArbnErfG §§ 5-7 — Pflichten im Überblick",
    title: "Pflichten von Arbeitgeber und Arbeitnehmer nach §§ 5-7 ArbnErfG (Mehrfachauswahl)",
    prompt:
      "Welche der folgenden Aussagen über die Pflichten der Parteien nach §§ 5-7 ArbnErfG sind zutreffend? (Mehrere Antworten möglich)",
    options: [
      "Der Arbeitnehmer muss die Diensterfindung gesondert in Textform (§ 126b BGB) melden (§ 5 Abs. 1 ArbnErfG)",
      "Der Arbeitgeber muss den Eingangszeitpunkt der Meldung unverzüglich in Textform bestätigen (§ 5 Abs. 3 ArbnErfG)",
      "Der Arbeitgeber kann die Inanspruchnahme auf einzelne Teile der Erfindung beschränken",
      "Mit der Inanspruchnahme (oder Fiktion) gehen alle Rechte an der Erfindung auf den Arbeitgeber über (§ 7 Abs. 1 ArbnErfG)",
    ],
    answers: [0, 1, 3],
    explanation:
      "§ 5 Abs. 1 ArbnErfG: Meldepflicht des Arbeitnehmers — gesondert und in Textform (§ 126b BGB), keine eigenhändige Unterschrift erforderlich (Antwort A korrekt). § 5 Abs. 3 ArbnErfG: Bestätigung des Eingangszeitpunkts durch den Arbeitgeber in Textform (Antwort B korrekt). § 7 Abs. 1 ArbnErfG: Vollständiger Rechtsübergang mit Inanspruchnahme (Antwort D korrekt). Antwort C ist falsch: Das frühere Recht erlaubte eine 'beschränkte Inanspruchnahme', bei der dem Arbeitnehmer Teilrechte verbleiben konnten — dieses Instrument ist mit der Reform 2009 entfallen. Seit 2009 gibt es nur noch volle Inanspruchnahme oder Freigabe.",
    // FIX (verifiziert): Optionen A + B: "schriftlich" → "in Textform (§ 126b BGB)";
    // Erklärung entsprechend angepasst; bgb126b als source ergänzt.
    sourceIds: ["arbnerfg5", "arbnerfg6", "arbnerfg7", "bgb126b"],
    sources: [
      {
        id: "arbnerfg5",
        label: "§ 5 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__5.html",
      },
      {
        id: "arbnerfg6",
        label: "§ 6 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__6.html",
      },
      {
        id: "arbnerfg7",
        label: "§ 7 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__7.html",
      },
      {
        id: "bgb126b",
        label: "§ 126b BGB",
        url: "https://www.gesetze-im-internet.de/bgb/__126b.html",
      },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Pflichten",
    skillTags: ["arbnerf", "meldepflicht", "inanspruchnahme", "rechtsuebergang"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-032",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "multi",
    topic: "ArbnErfG § 6 — Freistellungserklärung und ihre Folgen (Fall)",
    title: "Fall: Auswirkungen der Freigabe auf verschiedene Rechte (Mehrfachauswahl)",
    prompt:
      "Arbeitgeber AG gibt eine ordnungsgemäß gemeldete Diensterfindung von Arbeitnehmer A in Textform nach § 6 Abs. 2 ArbnErfG frei. Welche der folgenden Aussagen sind nach der Freigabe zutreffend? (Mehrere Antworten möglich)",
    options: [
      "A hat keinen Vergütungsanspruch aus § 9 ArbnErfG, weil AG die Erfindung freigegeben hat",
      "Die Erfindung wird zur freien Erfindung; A kann sie ohne weitere Bindungen an AG verwerten",
      "AG behält ein gesetzliches einfaches Nutzungsrecht an der freigegebenen Erfindung",
      "A muss die freigegebene Erfindung nach §§ 18-19 ArbnErfG AG erneut anbieten, bevor er sie Dritten lizenziert",
    ],
    answers: [0, 1],
    explanation:
      "§ 6 Abs. 2 ArbnErfG: Mit der Freigabe in Textform (§ 126b BGB) verzichtet AG auf alle Rechte aus der Inanspruchnahme. Da AG die Erfindung nicht in Anspruch nimmt, entsteht kein Vergütungsanspruch nach § 9 ArbnErfG — der Vergütungsanspruch setzt eine wirksame Inanspruchnahme voraus (Antwort A korrekt). Die Erfindung wird zur freien Erfindung und A kann sie verwerten (Antwort B korrekt). Antwort C ist falsch: Ein gesetzliches Nutzungsrecht verbleibt nach Freigabe nicht bei AG. Antwort D ist falsch kraft Gesetzes: § 8 Abs. 2 ArbnErfG ordnet ausdrücklich an, dass §§ 18 und 19 ArbnErfG für freigewordene Diensterfindungen nicht gelten.",
    // FIX (verifiziert): Prompt: "schriftlich" → "in Textform (§ 126b BGB)";
    // Erklärung D: "nach h.M." → "kraft Gesetzes (§ 8 Abs. 2 ArbnErfG)";
    // arbnerfg8 + bgb126b als sources ergänzt.
    sourceIds: ["arbnerfg6", "arbnerfg8", "arbnerfg9", "bgb126b"],
    sources: [
      {
        id: "arbnerfg6",
        label: "§ 6 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__6.html",
      },
      {
        id: "arbnerfg8",
        label: "§ 8 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__8.html",
      },
      {
        id: "arbnerfg9",
        label: "§ 9 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__9.html",
      },
      {
        id: "bgb126b",
        label: "§ 126b BGB",
        url: "https://www.gesetze-im-internet.de/bgb/__126b.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Rechtsfolge",
    skillTags: ["arbnerf", "freigabe", "inanspruchnahme", "verguetung"],
    reviewStatus: "reviewed",
  },

  // ─── ORDERING da-arbnerf-033 … da-arbnerf-036 ──────────────────────────────

  {
    id: "da-arbnerf-033",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "ordering",
    topic: "ArbnErfG §§ 5-9 — Verfahrensablauf Diensterfindung",
    title: "Prüfungsschema: Ablauf Diensterfindung von der Entstehung bis zur Vergütung",
    prompt:
      "Bringen Sie die folgenden Stationen im gesetzlichen Ablauf einer Diensterfindung von der Entstehung bis zum Vergütungsanspruch in die richtige Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "Entstehung der Erfindung (ggf. Abgrenzung Dienst- vs. freie Erfindung, § 4 ArbnErfG)",
      "Gesonderte Meldung in Textform durch den Arbeitnehmer (§ 5 Abs. 1 ArbnErfG i.V.m. § 126b BGB)",
      "Eingangsbestätigung durch Arbeitgeber in Textform; ggf. Beanstandung binnen 2 Monaten (§ 5 Abs. 3 ArbnErfG)",
      "Ablauf der 4-Monats-Frist ohne Freigabe in Textform → fiktive Inanspruchnahme (§ 6 Abs. 2 ArbnErfG)",
      "Rechtsübergang aller Rechte auf den Arbeitgeber (§ 7 Abs. 1 ArbnErfG)",
      "Inländische Schutzrechtsanmeldung durch Arbeitgeber (§ 13 ArbnErfG)",
      "Festsetzung und Zahlung der angemessenen Vergütung (§§ 9-12 ArbnErfG)",
    ],
    explanation:
      "Der gesetzliche Ablauf nach ArbnErfG: (1) § 4 ArbnErfG: Abgrenzung Diensterfindung. (2) § 5 Abs. 1 ArbnErfG: Meldepflicht des Arbeitnehmers — gesondert, in Textform (§ 126b BGB), unverzüglich. (3) § 5 Abs. 3 ArbnErfG: Eingangsbestätigung in Textform; AG muss Meldungsmängel binnen 2 Monaten in Textform beanstanden (sonst Heilungsfiktion). (4) § 6 Abs. 2 ArbnErfG: Ablauf der 4-Monats-Frist ohne Freigabe in Textform = fiktive Inanspruchnahme. (5) § 7 Abs. 1 ArbnErfG: Rechtsübergang auf AG. (6) § 13 ArbnErfG: Anmeldepflicht im Inland. (7) §§ 9-12 ArbnErfG: Vergütungsfestsetzung. Diese Reihenfolge ist das Kernprüfungsschema für ArbnErfG-Klausuraufgaben.",
    // FIX (verifiziert): orderItems Nr. 2/3/4 + Erklärung: "schriftlich" → "in Textform
    // (§ 126b BGB)"; Heilungsfiktion (2-Monats-Beanstandungsfrist) in Nr. 3 ergänzt.
    sourceIds: ["arbnerfg4", "arbnerfg5", "arbnerfg6", "arbnerfg7", "arbnerfg9", "arbnerfg13", "bgb126b"],
    sources: [
      {
        id: "arbnerfg4",
        label: "§ 4 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__4.html",
      },
      {
        id: "arbnerfg5",
        label: "§ 5 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__5.html",
      },
      {
        id: "arbnerfg6",
        label: "§ 6 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__6.html",
      },
      {
        id: "arbnerfg9",
        label: "§ 9 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__9.html",
      },
      {
        id: "arbnerfg13",
        label: "§ 13 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__13.html",
      },
      {
        id: "bgb126b",
        label: "§ 126b BGB",
        url: "https://www.gesetze-im-internet.de/bgb/__126b.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["arbnerf", "pruefungsschema", "inanspruchnahme", "meldepflicht", "verguetung"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-034",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "ordering",
    topic: "ArbnErfG §§ 28, 34, 37 — Schiedsstellen-Verfahren",
    title: "Ablauf des Schiedsstellen-Verfahrens beim DPMA (§§ 28, 34, 37 ArbnErfG)",
    prompt:
      "Bringen Sie die Stationen eines Schiedsstellen-Verfahrens in die richtige Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "Entstehung eines Streits zwischen Arbeitnehmer und Arbeitgeber über Rechte aus einer Erfindung",
      "Zwingend: Anrufung der Schiedsstelle beim DPMA vor Klageerhebung (§ 37 Abs. 1 ArbnErfG)",
      "Durchführung des Schiedsstellenverfahrens; Anhörung beider Parteien",
      "Unterbreitung eines Einigungsvorschlags durch die Schiedsstelle (§ 34 ArbnErfG)",
      "Annahme oder Ablehnung des Einigungsvorschlags durch die Parteien (Widerspruchsfrist: 1 Monat, schriftlich)",
      "Bei Scheitern: Klageerhebung vor dem zuständigen Gericht (§ 37 ArbnErfG)",
    ],
    explanation:
      "§§ 28, 34, 37 ArbnErfG: (1) Streitentstehung. (2) § 37 Abs. 1 ArbnErfG: Zwingend vorgeschaltetes Schiedsstellenverfahren beim DPMA — ohne Anrufung ist eine Klage unzulässig (prozessuale Zulässigkeitsvoraussetzung). (3) Verfahrensdurchführung. (4) § 34 ArbnErfG: Einigungsvorschlag der Schiedsstelle — gilt als angenommen, wenn keine Partei binnen eines Monats schriftlich widerspricht. (5) Annahme/Ablehnung — widerspricht eine Partei, gilt der Vorschlag als nicht gemacht, kann aber als Angebot im Prozess verwendet werden. (6) Bei gescheiterter Einigung: Klage vor Gericht (§ 37 ArbnErfG; §§ 29/30 regeln nur die Besetzung der Schiedsstelle). Die zwingende Vorgängigkeit der Schiedsstelle ist das wichtigste Verfahrensmerkmal des ArbnErfG.",
    // FIX (verifiziert): orderItems Nr. 2/4/6 + Erklärung + Titel:
    // "§ 29 Abs. 1" → "§ 37 Abs. 1"; "§ 30" → "§ 34"; "§ 29 Abs. 2" → "§ 37";
    // sourceIds: arbnerfg29/arbnerfg30 → arbnerfg34/arbnerfg37.
    sourceIds: ["arbnerfg28", "arbnerfg34", "arbnerfg37"],
    sources: [
      {
        id: "arbnerfg28",
        label: "§ 28 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__28.html",
      },
      {
        id: "arbnerfg34",
        label: "§ 34 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__34.html",
      },
      {
        id: "arbnerfg37",
        label: "§ 37 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__37.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["arbnerf", "schiedsstelle", "verfahren"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-035",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "ordering",
    topic: "ArbnErfG §§ 13-16 — Vom Schutzrecht zur Aufgabe",
    title: "Ablauf: Schutzrechtsanmeldung bis zur Anbietungspflicht bei Aufgabe",
    prompt:
      "Bringen Sie die Schritte im Umgang mit dem Schutzrecht nach der Inanspruchnahme bis zur möglichen Aufgabe in die richtige Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "Inanspruchnahme der Diensterfindung durch den Arbeitgeber (§ 6 ArbnErfG)",
      "Unverzügliche inländische Schutzrechtsanmeldung durch den Arbeitgeber (§ 13 ArbnErfG)",
      "Ggf. Auslandsanmeldungen; Unterrichtung des Arbeitnehmers (§ 14 ArbnErfG)",
      "Aufrechterhaltung des Schutzrechts und Vergütungszahlung (§§ 9-12 ArbnErfG)",
      "Entscheidung des Arbeitgebers, das Schutzrecht aufzugeben",
      "Anbietungspflicht: rechtzeitige Mitteilung und Angebot der Übertragung an den Arbeitnehmer (§ 16 Abs. 1 ArbnErfG)",
    ],
    explanation:
      "§§ 13-16 ArbnErfG: Nach Inanspruchnahme (§ 6) muss der Arbeitgeber unverzüglich im Inland anmelden (§ 13). Auslandsanmeldungen sind möglich (§ 14). Das Schutzrecht wird aufrechterhalten und die Vergütung gezahlt (§§ 9-12). Entscheidet der Arbeitgeber zur Aufgabe, muss er dies dem Arbeitnehmer rechtzeitig mitteilen und das Schutzrecht zur Übernahme anbieten (§ 16 Abs. 1). Die Anbietungspflicht schützt den Arbeitnehmer davor, dass sein Patent ohne seine Kenntnis verfällt.",
    sourceIds: ["arbnerfg13", "arbnerfg14", "arbnerfg16"],
    sources: [
      {
        id: "arbnerfg13",
        label: "§ 13 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__13.html",
      },
      {
        id: "arbnerfg14",
        label: "§ 14 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__14.html",
      },
      {
        id: "arbnerfg16",
        label: "§ 16 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__16.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["arbnerf", "anmeldepflicht", "anbietungspflicht", "schutzrecht"],
    // REVIEW (question-reviewer): REVIEWED — Sequenz korrekt und eindeutig
    // (Inanspruchnahme § 6 → Inlandsanmeldung § 13 → Auslandsanmeldung § 14 →
    // Aufrechterhaltung/Vergütung §§ 9-12 → Aufgabeentscheidung → Anbietungspflicht
    // § 16 Abs. 1). Normzitate korrekt zugeordnet. orderItem 6 behauptet keine
    // falsche Form (nur "rechtzeitige Mitteilung und Angebot"). Format ok, sources
    // vorhanden. Freigabe.
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-036",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "ordering",
    topic: "ArbnErfG §§ 18-19 — Freie Erfindung mit Betriebsbezug",
    title: "Ablauf: Freie Erfindung mit Betriebsbezug (§§ 18-19 ArbnErfG) — Fall",
    prompt:
      "Arbeitnehmer A macht eine freie Erfindung mit sachlichem Betriebszusammenhang. Bringen Sie die Pflichtenstationen in die richtige gesetzliche Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "Entstehung der freien Erfindung mit sachlichem Zusammenhang zum Tätigkeitsbereich des Betriebs",
      "Mitteilung der Erfindung an den Arbeitgeber in Textform (§ 18 ArbnErfG i.V.m. § 126b BGB)",
      "Angebot eines nicht-ausschließlichen Nutzungsrechts an den Arbeitgeber in Textform (§ 19 ArbnErfG i.V.m. § 126b BGB)",
      "Annahme oder Ablehnung des Angebots durch den Arbeitgeber",
      "Bei Ablehnung: freie Verwertung durch den Arbeitnehmer, z.B. Lizenzierung an Dritte",
    ],
    explanation:
      "§§ 18-19 ArbnErfG: (1) Freie Erfindung mit Betriebsbezug. (2) § 18: Mitteilungspflicht — Unterrichtung des Arbeitgebers in Textform (§ 126b BGB) vor Verwertung; gilt bei jeder freien Erfindung (Ausnahme § 18 Abs. 3). (3) § 19: Anbietungspflicht (Trigger: Betriebsbezug) — Angebot einer einfachen (nicht-ausschließlichen) Lizenz in Textform. (4) Arbeitgeber entscheidet. (5) Bei Ablehnung kann A frei verwerten. Wichtig: Nur ein nicht-ausschließliches Nutzungsrecht ist anzubieten — keine Abtretungspflicht. Der Arbeitgeber erhält durch Annahme lediglich eine einfache Lizenz, kein ausschließliches Recht.",
    sourceIds: ["arbnerfg18", "arbnerfg19", "bgb126b"],
    sources: [
      {
        id: "arbnerfg18",
        label: "§ 18 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__18.html",
      },
      {
        id: "arbnerfg19",
        label: "§ 19 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__19.html",
      },
      {
        id: "bgb126b",
        label: "§ 126b BGB",
        url: "https://www.gesetze-im-internet.de/bgb/__126b.html",
      },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["arbnerf", "freie-erfindung", "anbietungspflicht", "mitteilungspflicht"],
    // FIX (verifiziert): orderItems Nr. 2/3: "Schriftliche" → "in Textform (§ 126b BGB)";
    // Erklärung: "schriftlich" → "in Textform"; § 18-Anwendungsbereich (jede freie
    // Erfindung) + Betriebsbezug als § 19-Trigger klargestellt; bgb126b ergänzt.
    reviewStatus: "reviewed",
  },

  // ─── CLOZE da-arbnerf-037 … da-arbnerf-038 ─────────────────────────────────

  {
    id: "da-arbnerf-037",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "cloze",
    topic: "ArbnErfG § 6 Abs. 2 — Fiktive Inanspruchnahme (Kernthema)",
    title: "Lücke: 4-Monats-Frist der fiktiven Inanspruchnahme",
    prompt:
      "Nach § 6 Abs. 2 ArbnErfG gilt eine Diensterfindung als in Anspruch genommen, wenn der Arbeitgeber sie nicht innerhalb von ___ Monaten nach Eingang der ordnungsgemäßen Meldung durch Erklärung in Textform freigibt.",
    options: ["2", "4", "6", "12"],
    answers: [1],
    // FIX (verifiziert): Stem: "durch schriftliche Erklärung" → "durch Erklärung in
    // Textform"; Erklärung: "schriftlich frei" → "in Textform frei"; bgb126b ergänzt.
    explanation:
      "§ 6 Abs. 2 ArbnErfG (seit 01.10.2009): Die gesetzliche Frist beträgt genau vier Monate ab Eingang der ordnungsgemäßen Meldung nach § 5 ArbnErfG. Gibt der Arbeitgeber die Erfindung nicht innerhalb dieser Frist durch Erklärung in Textform (§ 126b BGB) frei, gilt sie kraft Gesetzes als in Anspruch genommen (Fiktionswirkung). Die 4-Monats-Frist ist eine der prüfungsrelevantesten Fristen im deutschen Arbeitnehmererfindungsrecht — kein anderer Zeitraum ist korrekt.",
    sourceIds: ["arbnerfg6", "bgb126b"],
    sources: [
      {
        id: "arbnerfg6",
        label: "§ 6 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__6.html",
      },
      {
        id: "bgb126b",
        label: "§ 126b BGB",
        url: "https://www.gesetze-im-internet.de/bgb/__126b.html",
      },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["arbnerf", "inanspruchnahme", "4-monats-frist"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-arbnerf-038",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "cloze",
    topic: "ArbnErfG § 5 Abs. 1 — Meldepflicht: Form",
    title: "Lücke: Form der Diensterfindungsmeldung",
    prompt:
      "Nach § 5 Abs. 1 ArbnErfG ist der Arbeitnehmer verpflichtet, eine Diensterfindung dem Arbeitgeber unverzüglich durch eine ___ Meldung in Textform (§ 126b BGB) anzuzeigen.",
    options: ["formlose", "gesonderte", "notarielle", "doppelte"],
    answers: [1],
    // FIX (verifiziert): Stem: "schriftliche Meldung" → "Meldung in Textform (§ 126b BGB)";
    // Lücke "gesonderte" (Index 1) bleibt korrekt; bgb126b als source ergänzt.
    explanation:
      "§ 5 Abs. 1 ArbnErfG: Die Meldung muss 'gesondert' erfolgen. Das Merkmal 'gesondert' bedeutet, dass die Meldung als eigenes Dokument erkennbar und von anderen Informationen (Berichten, allgemeiner Korrespondenz) getrennt sein muss. Nur eine gesonderte Meldung versetzt den Arbeitgeber in die Lage, klar zu erkennen, dass eine Diensterfindung gemeldet wird — und setzt damit die 4-Monats-Frist des § 6 Abs. 2 ArbnErfG in Gang. Eine formlose, in allgemeinen Berichten eingebettete Mitteilung genügt nicht.",
    sourceIds: ["arbnerfg5", "bgb126b"],
    sources: [
      {
        id: "arbnerfg5",
        label: "§ 5 ArbnErfG",
        url: "https://www.gesetze-im-internet.de/arbnerfg/__5.html",
      },
      {
        id: "bgb126b",
        label: "§ 126b BGB",
        url: "https://www.gesetze-im-internet.de/bgb/__126b.html",
      },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Formerfordernis",
    skillTags: ["arbnerf", "meldepflicht", "textform"],
    reviewStatus: "reviewed",
  },
];
